import deepEqual from 'fast-deep-equal'
import React, {memo, useCallback} from 'react'

import {emptyArray, getEmptyHash} from '../../../../../../../../../utils/empty'
import {
  useDoesJobExist,
  useNonDeletedPipelineJobIdsOrDraft,
  usePipelineDraftGraph,
} from '../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {
  getDraftJobs,
  getOriginalPipelineSettings,
} from '../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {getDependencyName} from '../../../../../../EditPipelinePage/EditPipelinePage.utils'
import {pipelineDraft} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import SettingsBlock from '../../../../SettingsBlock/SettingsBlock'

import {JobDependenciesNote} from './JobDependenciesNote/JobDependenciesNote'
import JobDependency from './JobDependency/JobDependency'

import styles from './JobDependencies.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  jobId: string
}

function JobDependencies({jobId}: Props) {
  const dispatch = useAppDispatch()
  const pipelineId = usePipelineId()
  const jobs = useNonDeletedPipelineJobIdsOrDraft()
  const graph = usePipelineDraftGraph()
  const {dependencies, successors} = graph[jobId] ?? {
    dependencies: new Set(),
    successors: new Set(),
  }
  const filesDependencies = useAppSelector(
    state => getDraftJobs(state, pipelineId)?.[jobId]?.dependencies,
  )
  const isExisting = useDoesJobExist(jobId)
  const savedDependencies =
    useAppSelector(getOriginalPipelineSettings(pipelineId))?.jobs?.[jobId]?.dependencies ??
    emptyArray
  const pipelineJobs = useAppSelector(state => getDraftJobs(state, pipelineId)) ?? getEmptyHash()
  const getJobFilesPublication = useCallback(
    (id: string) => pipelineJobs?.[id]?.['files-publication'],
    [pipelineJobs],
  )
  const possibleDependencies = jobs.filter(id => id !== jobId && !successors.has(id))
  const handleDependencyCheckboxChange =
    (dependencyId: string) =>
    ({currentTarget}: React.ChangeEvent<HTMLInputElement>) => {
      const {checked} = currentTarget
      const files = getJobFilesPublication(dependencyId) ?? []

      dispatch(
        pipelineDraft.actions.setDependencies({
          id: pipelineId,
          jobId,
          dependencies: checked
            ? [...dependencies, dependencyId]
            : [...dependencies].filter(otherId => otherId !== dependencyId),
        }),
      )

      // Task: TCP-22 Rework "Use files..." link on Dependencies block
      // Auto Assign publication files from the dependent job
      if (checked && files.length) {
        dispatch(
          pipelineDraft.actions.setFilesDependencies({
            id: pipelineId,
            jobId,
            dependencyId,
            files,
          }),
        )
      }
    }

  if (!possibleDependencies.length) {
    return null
  }

  return (
    <SettingsBlock
      id="dependencies"
      collapsible
      collapsedDefault={false}
      collapsedNote={<JobDependenciesNote dependencies={Array.from(dependencies)} />}
      title="Dependencies"
    >
      <div className={styles.dependencies}>
        {possibleDependencies.map((depId, index) => {
          const enabled = dependencies.has(depId)
          const wasEnabled = savedDependencies.some(dep => getDependencyName(dep) === depId)
          const files = filesDependencies?.[index]
          const sevedFiles = savedDependencies[index]
          const isFilesEqual = deepEqual(sevedFiles, files)
          const isUnsaved = (isExisting && enabled !== wasEnabled) || !isFilesEqual

          return (
            <JobDependency
              onEnabled={handleDependencyCheckboxChange(depId)}
              jobId={jobId}
              key={depId}
              depId={depId}
              enabled={enabled}
              unsaved={isUnsaved}
            />
          )
        })}
      </div>
    </SettingsBlock>
  )
}

export default memo<Props>(JobDependencies)
